import apiClient from './client';
import { env } from '@/config/env';
import { useAuthStore } from '../store/authStore';
import { tokenRequest } from '@/config/auth';
import { logToken } from '../utils/jwt';
import type { OpenAIResponseRequest, OpenAIResponseOutput } from '@/types/api';
import type { StreamEvent } from '../streaming/types';
import type { InputContent } from '@/types/files';

const API_VERSION = 'v2';

/**
 * Build structured input for messages with files
 */
export function buildStructuredInput(
  text: string,
  fileIds: string[]
): InputContent[] {
  const content: InputContent[] = [];

  // Add text content if present
  if (text.trim()) {
    content.push({ type: 'input_text', text: text.trim() });
  }

  // Add file references
  for (const fileId of fileIds) {
    content.push({ type: 'input_file', fileId });
  }

  return content;
}

/**
 * Send a message (non-streaming)
 *
 * POST /openai/v2/responses
 */
export async function sendMessage(
  input: string | InputContent[],
  conversationId?: string
): Promise<OpenAIResponseOutput> {
  const request: OpenAIResponseRequest = {
    input,
    conversation: conversationId,
  };

  const response = await apiClient.post(
    `/openai/${API_VERSION}/responses`,
    request
  );
  return response.data;
}

/**
 * Send a message with streaming response
 *
 * POST /openai/v2/responses/stream
 *
 * @param input - User message (string or structured input with files)
 * @param conversationId - Optional conversation ID
 * @param onEvent - Callback for each streaming event
 * @param onComplete - Callback when stream completes
 * @param onError - Callback on error
 */
export async function sendStreamingMessage(
  input: string | InputContent[],
  conversationId: string | undefined,
  onEvent: (event: StreamEvent) => void,
  onComplete: () => void,
  onError: (error: Error) => void
): Promise<void> {
  const { msalInstance, isAuthenticated } = useAuthStore.getState();

  if (!isAuthenticated || !msalInstance) {
    throw new Error('User not authenticated. Please log in to send messages.');
  }

  // Prefer active account, fallback to first cached account
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No accounts found. Please log in again.');
  }

  // Acquire token for backend API
  let accessToken: string;
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account,
    });
    accessToken = response.accessToken;

    // LOG TOKEN: For SSE streaming request (dev only)
    if (import.meta.env.DEV) {
      logToken(accessToken, 'SSE STREAMING v2');
    }
  } catch (error) {
    console.error('Failed to acquire token for streaming:', error);
    throw new Error('Authentication failed. Please log in again.');
  }

  // Make streaming request
  const response = await fetch(
    `${env.apiBaseUrl}/openai/${API_VERSION}/responses/stream`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        input,
        conversation: conversationId,
        // Reasoning params could be passed here in the future
        // reasoning: { effort: 'medium', summary: 'auto' },
        // verbosity: 'medium',
      } satisfies OpenAIResponseRequest),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();

    // Handle 401 with unauthorized tenant
    if (response.status === 401) {
      const { logout, setError } = useAuthStore.getState();
      const isUnauthorizedTenant = errorText.toLowerCase().includes('unauthorized tenant');

      if (isUnauthorizedTenant) {
        setError(
          'Your organization is not authorized to use this application. Please contact the administrator.'
        );
        logout();
        onError(new Error('Unauthorized tenant'));
        return;
      }

      // Other 401 - token expired
      logout();
    }

    onError(new Error(`HTTP ${response.status}: ${errorText}`));
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError(new Error('No response body'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6); // Remove 'data: ' prefix

        if (data === '[DONE]') {
          onComplete();
          return;
        }

        try {
          const event: StreamEvent = JSON.parse(data);
          onEvent(event);
        } catch (e) {
          console.error('Failed to parse SSE event:', e, data);
        }
      }
    }

    // Stream ended without [DONE]
    onComplete();
  } catch (error) {
    onError(error instanceof Error ? error : new Error('Stream failed'));
  } finally {
    reader.releaseLock();
  }
}
